/**
 * Analysis functions for European energy price data
 */

import type {
	ParsedEuroPriceRecord,
	ParsedEuroLoadRecord,
} from "../types/euroData";

export interface AveragePriceResult {
	/** Time-weighted average price in EUR/MWh */
	averagePrice: number;
	/** Total duration covered in milliseconds */
	totalDurationMs: number;
	/** Number of records included in the calculation */
	recordCount: number;
	/** Start of the actual time range (earliest record start) */
	actualStartTime: Date;
	/** End of the actual time range (latest record end) */
	actualEndTime: Date;
}

export interface IntermittentCapacity {
	/** Capacity in MW available during the specified hours */
	capacity: number;
	/** Start hour (0-23, UTC) when capacity is available */
	startTime: number;
	/** End hour (0-23, UTC) when capacity is available */
	endTime: number;
	/** Cost to generate/use this capacity in EUR/MWh */
	price: number;
}

export interface SimulationConfig {
	/** Fixed cost to generate energy in EUR/MWh (also known as PPA price) */
	generationCost: number;
	/** Base load capacity in MW (load below this is handled by other sources) */
	baseCapacityMW: number;
	/** Plant generation capacity in MW (maximum power we can generate/sell) */
	plantCapacityMW: number;
	/** Which market price to use for simulation */
	priceType: "dayAhead" | "intraday";
	/** If true, treat negative prices as 0 (no rebate). If false, use actual negative prices */
	noNegativeRebate: boolean;
	/** Array of intermittent capacity sources (e.g., solar) available during specific hours */
	intermittentCapacity?: IntermittentCapacity[];
}

export interface SimulationResult {
	/** Total savings from buying when market price < generation cost (EUR) */
	savings: number;
	/** Total revenue from selling when market price >= generation cost (EUR) */
	revenue: number;
	/** Total profit (savings + revenue) in EUR */
	profit: number;
	/** Total energy bought from the market in MWh */
	totalEnergyBought: number;
	/** Total energy sold to the market in MWh */
	totalEnergySold: number;
	/** Total energy generated from intermittent capacity in MWh */
	totalIntermittentGenerated: number;
	/** Total energy from intermittent capacity sold to market in MWh */
	totalIntermittentSold: number;
	/** Total energy generated by the fixed plant in MWh */
	totalPlantGenerated: number;
	/** Revenue from plant generation to meet internal load (EUR) */
	totalPlantRevenueFromLoad: number;
	/** Revenue from plant generation sold to market (EUR) */
	totalPlantRevenueFromMarket: number;
	/** Number of records included in the calculation */
	recordCount: number;
	/** Start of the actual time range (earliest record start) */
	actualStartTime: Date;
	/** End of the actual time range (latest record end) */
	actualEndTime: Date;
}

/**
 * Calculate time-weighted average price for a given time range.
 *
 * This function properly handles records of different durations (e.g., 15-minute vs 1-hour records)
 * by weighting each record's price by its duration. This ensures that longer time periods
 * have proportionally more influence on the average.
 *
 * @param data - Array of parsed price records
 * @param startDate - Start of the time range (inclusive)
 * @param endDate - End of the time range (inclusive)
 * @param priceType - Which price to use: 'dayAhead' or 'intraday' (defaults to 'dayAhead')
 * @param noNegativeRebate - If true, treat negative prices as $0 (no rebate). If false, use actual negative prices (defaults to false)
 * @returns Average price result with metadata, or null if no records in range
 *
 * @example
 * ```typescript
 * const result = calculateTimeWeightedAveragePrice(
 *   data,
 *   new Date('2023-01-01T00:00:00Z'),
 *   new Date('2023-01-01T23:59:59Z')
 * );
 *
 * console.log(`Average price: ${result.averagePrice} EUR/MWh`);
 * console.log(`Based on ${result.recordCount} records`);
 * ```
 */
export function calculateTimeWeightedAveragePrice(
	data: ParsedEuroPriceRecord[],
	startDate: Date,
	endDate: Date,
	priceType: "dayAhead" | "intraday" = "dayAhead",
	noNegativeRebate: boolean = false,
): AveragePriceResult | null {
	// Filter records that overlap with the requested time range
	const relevantRecords = data.filter((record) => {
		return record.startTime <= endDate && record.endTime >= startDate;
	});

	if (relevantRecords.length === 0) {
		return null;
	}

	// Calculate time-weighted sum
	let weightedSum = 0;
	let totalDuration = 0;

	for (const record of relevantRecords) {
		// Get the price for this record
		let price =
			priceType === "intraday"
				? (record.intradayPrice ?? record.dayAheadPrice)
				: record.dayAheadPrice;

		// Handle negative prices based on noNegativeRebate option
		if (noNegativeRebate && price < 0) {
			price = 0;
		}

		// Calculate the overlap between the record and the requested range
		const overlapStart = new Date(
			Math.max(record.startTime.getTime(), startDate.getTime()),
		);
		const overlapEnd = new Date(
			Math.min(record.endTime.getTime(), endDate.getTime()),
		);

		// Duration of this record's overlap in milliseconds
		const duration = overlapEnd.getTime() - overlapStart.getTime();

		// Add weighted price contribution
		weightedSum += price * duration;
		totalDuration += duration;
	}

	// Calculate the time-weighted average
	const averagePrice = weightedSum / totalDuration;

	// Get actual time range covered
	const sortedRecords = [...relevantRecords].sort(
		(a, b) => a.startTime.getTime() - b.startTime.getTime(),
	);
	const actualStartTime = sortedRecords[0].startTime;
	const actualEndTime = sortedRecords[sortedRecords.length - 1].endTime;

	return {
		averagePrice,
		totalDurationMs: totalDuration,
		recordCount: relevantRecords.length,
		actualStartTime,
		actualEndTime,
	};
}

/**
 * Simulate market interaction to calculate savings and revenue.
 *
 * This function simulates a power plant's interaction with the market:
 * - When market price > PPA price: Sell excess capacity to market (if plant capacity > observed load)
 * - When market price < PPA price: Shut down plant and buy energy from market (savings)
 * - Intermittent capacity (e.g., solar) is available only during specified hours and affects both scenarios
 *
 * Observed load = Actual load - Base capacity (what we're responsible for handling)
 *
 * With intermittent capacity:
 * - When market price < generation cost: Use intermittent capacity first if cheaper than market, then buy remainder
 * - When market price >= generation cost: Sell all available intermittent capacity, then sell plant excess
 *
 * @param priceData - Array of parsed price records
 * @param loadData - Array of parsed load records
 * @param startDate - Start of the time range (inclusive)
 * @param endDate - End of the time range (inclusive)
 * @param config - Simulation configuration including generation cost, capacities, price type, and optional intermittent capacity
 * @returns Simulation result with savings, revenue, profit, and energy totals, or null if no records in range
 *
 * @example
 * ```typescript
 * const result = simulateMarket(
 *   priceData,
 *   loadData,
 *   new Date('2023-01-01T00:00:00Z'),
 *   new Date('2023-12-31T23:59:59Z'),
 *   {
 *     generationCost: 120, // EUR/MWh (PPA price)
 *     baseCapacityMW: 1000, // MW
 *     plantCapacityMW: 500, // MW
 *     priceType: 'dayAhead',
 *     noNegativeRebate: false,
 *     intermittentCapacity: [
 *       {
 *         capacity: 200, // MW
 *         startTime: 8, // 8:00 UTC
 *         endTime: 16, // 16:00 UTC (4 PM)
 *         price: 50 // EUR/MWh (solar generation cost)
 *       }
 *     ]
 *   }
 * );
 *
 * console.log(`Total profit: ${result.profit} EUR`);
 * console.log(`Savings: ${result.savings} EUR, Revenue: ${result.revenue} EUR`);
 * console.log(`Intermittent energy generated: ${result.totalIntermittentGenerated} MWh`);
 * ```
 */
export function simulateMarket(
	priceData: ParsedEuroPriceRecord[],
	loadData: ParsedEuroLoadRecord[],
	startDate: Date,
	endDate: Date,
	config: SimulationConfig,
): SimulationResult | null {
	// Filter price records that overlap with the requested time range
	const relevantPriceRecords = priceData.filter((record) => {
		return record.startTime <= endDate && record.endTime >= startDate;
	});

	// Filter load records that overlap with the requested time range
	const relevantLoadRecords = loadData.filter((record) => {
		return record.startTime <= endDate && record.endTime >= startDate;
	});

	if (relevantPriceRecords.length === 0 || relevantLoadRecords.length === 0) {
		return null;
	}

	// Create a map of load records by time for efficient lookup
	const loadMap = new Map<number, ParsedEuroLoadRecord>();
	for (const loadRecord of relevantLoadRecords) {
		loadMap.set(loadRecord.startTime.getTime(), loadRecord);
	}

	// Helper function to get available intermittent capacity at a given time
	const getIntermittentCapacity = (timestamp: Date): { capacity: number; price: number } => {
		if (!config.intermittentCapacity || config.intermittentCapacity.length === 0) {
			return { capacity: 0, price: 0 };
		}

		const hour = timestamp.getUTCHours();
		let totalCapacity = 0;
		let weightedPriceSum = 0;

		for (const source of config.intermittentCapacity) {
			// Check if current hour is within the source's active window
			if (hour >= source.startTime && hour < source.endTime) {
				totalCapacity += source.capacity;
				weightedPriceSum += source.capacity * source.price;
			}
		}

		const avgPrice = totalCapacity > 0 ? weightedPriceSum / totalCapacity : 0;
		return { capacity: totalCapacity, price: avgPrice };
	};

	// Initialize accumulators
	let totalSavings = 0;
	let totalRevenue = 0;
	let totalEnergyBought = 0;
	let totalEnergySold = 0;
	let totalIntermittentGenerated = 0;
	let totalIntermittentSold = 0;
	let totalPlantGenerated = 0;
	let totalPlantRevenueFromLoad = 0;
	let totalPlantRevenueFromMarket = 0;
	let recordsProcessed = 0;

	// Process each price record
	for (const priceRecord of relevantPriceRecords) {
		// Find matching load record
		const loadRecord = loadMap.get(priceRecord.startTime.getTime());
		if (!loadRecord || loadRecord.actualLoad === undefined) {
			// Skip if no matching load data
			continue;
		}

		// Get the market price for this record
		let marketPrice =
			config.priceType === "intraday"
				? (priceRecord.intradayPrice ?? priceRecord.dayAheadPrice)
				: priceRecord.dayAheadPrice;

		// Handle negative prices based on noNegativeRebate option
		if (config.noNegativeRebate && marketPrice < 0) {
			marketPrice = 0;
		}

		// Calculate the overlap between the record and the requested range
		const overlapStart = new Date(
			Math.max(priceRecord.startTime.getTime(), startDate.getTime()),
		);
		const overlapEnd = new Date(
			Math.min(priceRecord.endTime.getTime(), endDate.getTime()),
		);

		// Duration of this record's overlap in hours
		const durationMs = overlapEnd.getTime() - overlapStart.getTime();
		const durationHours = durationMs / (1000 * 60 * 60);

		// Calculate observed load: what we're responsible for handling
		const observedLoadMW = loadRecord.actualLoad - config.baseCapacityMW;

		// Skip if observed load is negative (base capacity covers all load)
		if (observedLoadMW <= 0) {
			continue;
		}

		// Get available intermittent capacity for this time period
		const intermittent = getIntermittentCapacity(priceRecord.startTime);

		// Determine if we buy or sell
		if (marketPrice < config.generationCost) {
			// Market price < PPA price: Consider using intermittent capacity or buying from market

			let remainingLoad = observedLoadMW;

			// Option 1: Use intermittent capacity if available and cheaper than market
			if (intermittent.capacity > 0 && intermittent.price < marketPrice) {
				const intermittentUsed = Math.min(intermittent.capacity, remainingLoad);
				const energyGenerated = intermittentUsed * durationHours; // MWh
				const savingsPerMWh = config.generationCost - intermittent.price;
				totalSavings += savingsPerMWh * energyGenerated;
				totalIntermittentGenerated += energyGenerated;
				remainingLoad -= intermittentUsed;
			}

			// Option 2: Buy remaining load from market if any remains
			if (remainingLoad > 0) {
				const savingsPerMWh = config.generationCost - marketPrice;
				const energyBought = remainingLoad * durationHours; // MWh
				totalSavings += savingsPerMWh * energyBought;
				totalEnergyBought += energyBought;
			}
		} else {
			// Market price >= PPA price: Generate from plant and sell any excess capacity

			// First, use intermittent capacity to sell to market if available
			if (intermittent.capacity > 0) {
				const revenuePerMWh = marketPrice - intermittent.price;
				const energySold = intermittent.capacity * durationHours; // MWh
				totalRevenue += revenuePerMWh * energySold;
				totalIntermittentSold += energySold;
				totalIntermittentGenerated += energySold;
			}

			// Second, generate from plant to meet observed load
			const plantGenerationForLoad = Math.min(config.plantCapacityMW, observedLoadMW);
			const energyGeneratedForLoad = plantGenerationForLoad * durationHours; // MWh
			totalPlantGenerated += energyGeneratedForLoad;
			totalPlantRevenueFromLoad += energyGeneratedForLoad * config.generationCost;

			// Third, check if plant has sellable excess capacity
			const sellableCapacityMW = config.plantCapacityMW - observedLoadMW;

			if (sellableCapacityMW > 0) {
				// We have excess capacity to sell to the market
				const revenuePerMWh = marketPrice - config.generationCost;
				const energySold = sellableCapacityMW * durationHours; // MWh
				totalRevenue += revenuePerMWh * energySold;
				totalEnergySold += energySold;
				totalPlantGenerated += energySold;
				totalPlantRevenueFromMarket += revenuePerMWh * energySold;
			}
			// If sellableCapacityMW <= 0, we generate just enough for our observed load, no revenue
		}

		recordsProcessed++;
	}

	if (recordsProcessed === 0) {
		return null;
	}

	// Get actual time range covered
	const sortedRecords = [...relevantPriceRecords].sort(
		(a, b) => a.startTime.getTime() - b.startTime.getTime(),
	);
	const actualStartTime = sortedRecords[0].startTime;
	const actualEndTime = sortedRecords[sortedRecords.length - 1].endTime;

	return {
		savings: totalSavings,
		revenue: totalRevenue,
		profit: totalSavings + totalRevenue,
		totalEnergyBought,
		totalEnergySold,
		totalIntermittentGenerated,
		totalIntermittentSold,
		totalPlantGenerated,
		totalPlantRevenueFromLoad,
		totalPlantRevenueFromMarket,
		recordCount: recordsProcessed,
		actualStartTime,
		actualEndTime,
	};
}
